import { logger } from "@/utils/logger";
import { getDealerID } from './dealerDataUtils';
import { DealerManager } from './dealerManager';

/**
 * Opens a URL, respecting auto_expand_chat timing on mobile
 * On mobile: collapses chat immediately, then waits for auto_expand_chat seconds before navigating
 * On desktop: navigates immediately
 */
export const openURL = async (url: string) => {
  try {
    const isMobile = window.innerWidth <= 768;
    
    logger.log('🔗 openURL called:', { url, isMobile, width: window.innerWidth });
    
    // Dispatch event to collapse chat immediately (mobile only)
    if (isMobile) {
      logger.log('📱 Dispatching collapse event');
      window.dispatchEvent(new CustomEvent('d2c-collapse-chat'));
    }
    
    // Desktop behavior: navigate immediately
    if (!isMobile) {
      logger.log('💻 Desktop: navigating immediately');
      window.location.href = url;
      return;
    }
    
    // Mobile behavior: respect auto_expand_chat timing
    const dealer = DealerManager.getCurrentDealer();
    const autoExpandSeconds = dealer?.auto_expand_chat || 0;
    
    logger.log('📱 Mobile dealer settings:', { 
      dealerExists: !!dealer, 
      autoExpandSeconds,
      dealer_id: dealer?.dealer_id 
    });
    
    // If auto_expand_chat is not set or is 0, open in new tab
    if (!autoExpandSeconds || autoExpandSeconds <= 0) {
      logger.log('📱 Mobile: No auto_expand_chat set, opening in new tab');
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    
    // Wait for auto_expand_chat seconds before navigating
    logger.log(`📱 Mobile: Waiting ${autoExpandSeconds}s before navigating`);
    setTimeout(() => {
      logger.log('⏰ Timeout complete, navigating now');
      window.location.href = url;
    }, autoExpandSeconds * 1000);
    
  } catch (error) {
    console.error('Error opening URL:', error);
    // Fallback to direct navigation
    window.location.href = url;
  }
};

/**
 * Gets the target attribute based on dealer ID
 * Dealer ID 2 returns "_blank", all others return "_self"
 */
export const getTargetForDealer = async (): Promise<'_blank' | '_self'> => {
  try {
    const dealerId = await getDealerID();
    return dealerId === 2 ? '_blank' : '_self';
  } catch (error) {
    console.error('Error determining dealer ID, defaulting to current tab:', error);
    return '_self';
  }
};